import { textIncludes } from "../table";
import { mappedCustomerTypes } from "./mappedTs";
import { normalizeCrmStatusFilterValue } from "./crmStatusFilter";
import { getContactEvents } from "components/_dashboard/contacts/table/rows/ContactTableNestedRowEvents";
import {
  getContactCrmIsHot,
  getContactCrmStatus,
  getContactCustomerTypes
} from "../../@types/sharedSchema";
import type {
  CalendarEvent,
  CustomerType,
  RealEstateContact,
  ProcessedListingAddress
} from "../../@types/sharedSchema";
import type { ActiveFilters } from "utils/contacts/table";
import type { RealEstateTab } from "components/_dashboard/contacts/table/core/ContactTableTabs";

type FilteredContactsOptions = {
  search: string;
  events: CalendarEvent[] | null;
  activeFilters: ActiveFilters;
};

export type RealEstateContactWithRank = RealEstateContact & { _ranking?: number };

export function getFilteredContacts(
  contacts: RealEstateContact[],
  { search, events, activeFilters }: FilteredContactsOptions
): RealEstateContact[] {
  const currentActiveFilters = Object.entries(activeFilters).filter(
    ([id, { active }]) => id !== "sortBy" && active
  );

  const isNoFiltersActive = !search && !currentActiveFilters.length;

  if (isNoFiltersActive) return contacts;

  let filteredContacts: RealEstateContactWithRank[] = contacts.filter(
    (contact: RealEstateContactWithRank) => {
      const isMatchAllFilters = currentActiveFilters.every(([rawId, filter]) => {
        const { type, value } = filter;

        const parsedId = rawId as keyof ActiveFilters;

        if (parsedId === "hasMeetingOrTask") {
          if (typeof value !== "boolean") return true;

          const contactEvents = getContactEvents(contact.id, events, false);

          let isMatch = false;

          if (value) isMatch = contactEvents.length > 0;
          else isMatch = contactEvents.length === 0;

          return isMatch;
        }

        // ⚠️ THE PHONE COLUMN IS THE DOCUMENT ID. The same-named document field is a
        // legacy mirror that is absent on ~90% of contacts, so reading `contact[parsedId]`
        // here would make the phone filter match nothing — and, worse, make the "empty"
        // variant of that filter report every contact as having no phone at all.
        const columnValue = parsedId === "callerPhoneNumber" ? contact.id : contact[parsedId];

        if (isContactFilterEmpty(parsedId, activeFilters)) {
          if (Array.isArray(columnValue)) {
            return !columnValue.length;
          }

          return !columnValue;
        }

        const isNotValidValue = value === null || value === "";

        if (isNotValidValue) return true;

        if (type === "interestedInLocations") {
          const selectedAreas = (value as ProcessedListingAddress[]) ?? [];
          const contactAreas = (columnValue as ProcessedListingAddress[]) ?? [];

          if (!selectedAreas.length) return true;
          if (!contactAreas.length) return false;

          // Entity-level match: a contact matches if any of its interested
          // areas equals a selected area at the granularity that area defines
          // (city / neighborhood / street). No free-text word splitting — so
          // "Givat Makhosh" no longer matches "Givat Ram".
          return selectedAreas.some((selectedArea) =>
            contactAreas.some((contactArea) => isMatchingArea(selectedArea, contactArea))
          );
        }

        if (type === "text") {
          const isMatch = textIncludes(columnValue, value);

          return isMatch;
        }

        if (type === "rooms") {
          const { min, max } = value;

          const checkedRoomsFilter = ["roomsMin", "roomsMax"].some((key) => {
            const columnValue = contact[key];

            const checkBothRange = min && max && min !== max;

            if (checkBothRange) return columnValue >= min && columnValue <= max;

            if (min) return columnValue >= min;

            if (max) return columnValue <= max;

            if (!min || !max) return true;

            return false;
          });

          return checkedRoomsFilter;
        }

        if (type === "date" || type === "customerBudget" || type === "customerBudgetRent") {
          const { min, max } = value;

          if (min && max) return columnValue >= min && columnValue <= max;

          if (min) return columnValue >= min;

          if (max) return columnValue <= max;

          if (!min || !max) return true;
        }

        if (type === "tags" || type === "interestedInPropertyTypes") {
          const selectedValues = value as string[];
          const contactValues = columnValue as string[];

          if (!selectedValues || !selectedValues.length) return true;
          if (!contactValues || !contactValues.length) return false;

          return selectedValues.some((selectedValue) => contactValues.includes(selectedValue));
        }

        // A contact can hold several customer types — the (single-value) filter matches
        // if it's one of them. Normalized, so legacy single-type docs behave as before.
        if (type === "customerType") {
          return getContactCustomerTypes(contact).includes(value as CustomerType);
        }

        // The status filter (historical id "customerSeriousness") matches on the canonical
        // CRM v2 axes — crmStatus, or crmIsHot for the "hot" option — never on the legacy
        // mirror field. Persisted legacy Hebrew values normalize to their v2 equivalent.
        if (type === "customerSeriousness") {
          const statusFilter = normalizeCrmStatusFilterValue(value);

          if (!statusFilter) return true;

          if (statusFilter === "hot") return getContactCrmIsHot(contact);

          return getContactCrmStatus(contact) === statusFilter;
        }

        return columnValue === value;
      });

      if (!isMatchAllFilters) return false;

      const searchWords = search.trim().split(" ").filter(Boolean);

      if (!searchWords.length) return true;

      const {
        contactOrigin,
        callerLastName,
        callerFirstName,
        additionalNotes,
        id: contactPhone,
        contactOriginAdditionalInfo
      } = contact;

      let ranking = 0;

      let isMatchingCallerFirstName = false;
      let isMatchingCallerLastName = false;

      for (const searchWord of searchWords) {
        const isFirstNameMatch = textIncludes(callerFirstName, searchWord);
        const isLastNameMatch = textIncludes(callerLastName, searchWord);

        const isMatchPartOfName = isFirstNameMatch || isLastNameMatch;

        if (isMatchPartOfName) ranking++;

        if (isFirstNameMatch) isMatchingCallerFirstName = true;
        if (isLastNameMatch) isMatchingCallerLastName = true;
      }

      contact._ranking = ranking;

      let isMatchingCallerName = isMatchingCallerFirstName || isMatchingCallerLastName;

      if (isMatchingCallerName) return true;

      for (const searchWord of searchWords) {
        const isAdditionalNotesMatch = textIncludes(additionalNotes, searchWord);

        const parsedSearchWord = searchWord.replace(/^0(\d{2})/, "$1");
        const isPhoneNumberMatch = textIncludes(contactPhone, parsedSearchWord);

        const isContactOriginMatch = textIncludes(contactOrigin, searchWord);
        const isContactOriginAdditionalInfoMatch = textIncludes(
          contactOriginAdditionalInfo,
          searchWord
        );

        const isSearchWordMatch =
          isPhoneNumberMatch ||
          isAdditionalNotesMatch ||
          isContactOriginMatch ||
          isContactOriginAdditionalInfoMatch;

        if (isSearchWordMatch) return true;
      }

      return false;
    }
  );

  const highRankingContacts = getHighestRankingContactsBySearch(filteredContacts);

  if (highRankingContacts.length) {
    filteredContacts = highRankingContacts;
  }

  return filteredContacts;
}

/**
 * Entity-level comparison between a selected filter area and a contact's
 * interested area. The filter area only constrains the fields it actually
 * defines: a city-level selection (no neighborhood/street) matches any contact
 * area in that city, while a neighborhood/street selection must match those
 * fields exactly. This is the same field-subset semantics LocationPicker uses
 * to mark a suggestion as already-selected.
 */
function isMatchingArea(
  filterArea: ProcessedListingAddress,
  contactArea: ProcessedListingAddress
): boolean {
  return (
    (!filterArea.city || contactArea.city === filterArea.city) &&
    (!filterArea.neighborhood || contactArea.neighborhood === filterArea.neighborhood) &&
    (!filterArea.street || contactArea.street === filterArea.street)
  );
}

export function isContactFilterEmpty(
  filterId: keyof ActiveFilters,
  activeFilters: ActiveFilters
): boolean {
  const filter = activeFilters[filterId];

  const { type, empty } = filter;

  const isFilterIdCanBeEmpty = [
    "tags",
    "interestedInLocations",
    "interestedInPropertyTypes",
    "lastBulkMessageReceivedTime"
  ].includes(filterId);

  const isFilterTypeCanBeEmpty = ["text", "customerBudget"].includes(type);

  const isFieldCanBeEmpty = isFilterIdCanBeEmpty || isFilterTypeCanBeEmpty;

  const isEmptyTextType = isFieldCanBeEmpty && empty;

  return isEmptyTextType;
}

export function getFilteredContactsByType(
  contact: RealEstateContact[],
  type: RealEstateTab
): RealEstateContact[] {
  if (type === "all") return contact;

  if (type === "hotCustomer") {
    // Canonical hot-lead read (CRM v2) — every write path derives crmIsHot, including
    // legacy customerSeriousness-only writes, so the flag is authoritative.
    return contact.filter((c) => getContactCrmIsHot(c));
  }

  if (type === "leadsFromBot") {
    const newFilteredContacts = contact.filter(
      ({ contactOrigin }) => contactOrigin === "Whatsapp Automated Bot"
    );

    return newFilteredContacts;
  }

  const mappedCustomerType = Object.keys(mappedCustomerTypes).find(
    (key) => mappedCustomerTypes[key] === type
  ) as keyof typeof mappedCustomerTypes;

  // Normalized multi-type match: a buyer+seller contact appears in BOTH the buyer and
  // seller tabs (and is counted in both tab badges).
  const newFilteredContacts = contact.filter((c) =>
    getContactCustomerTypes(c).includes(mappedCustomerType as CustomerType)
  );

  return newFilteredContacts;
}

export function getHighestRankingContactsBySearch(
  contacts: RealEstateContactWithRank[]
): RealEstateContact[] {
  const contactsByHighestRanking = [...contacts].sort((a, b) => b._ranking - a._ranking);

  const highRankingContacts: RealEstateContact[] = [];

  let tempHighRanking: number;

  for (const contact of contactsByHighestRanking) {
    const { _ranking: ranking } = contact;

    if (ranking < 2) break;

    if (tempHighRanking && ranking < tempHighRanking) break;

    tempHighRanking = ranking;

    highRankingContacts.push(contact);
  }

  return highRankingContacts;
}
